/**
 * Service de dialogues — `window.alert/confirm/prompt` sont bannis (design system §8).
 *
 * Singleton et non contexte React : les hooks et les services non-composants doivent pouvoir
 * l'appeler. Repli natif si aucun hôte n'est monté, ce qui laisse passer les tests unitaires
 * de hooks isolés sans monter d'arbre React.
 */

let host = null;
let queue = [];
let sequence = 0;

/** Branché par `DialogHost` au montage. */
export function registerDialogHost(handler) {
  host = handler;
  // Un hôte monté après une demande en attente doit quand même la servir.
  const pending = queue;
  queue = [];
  pending.forEach((request) => host(request));
  return () => {
    host = null;
  };
}

function ask(request) {
  return new Promise((resolve) => {
    const enriched = { ...request, key: ++sequence, resolve };
    if (host) host(enriched);
    else if (typeof window === 'undefined') resolve(request.type === 'confirm' ? false : null);
    else {
      // Repli natif : uniquement quand aucun hôte n'est monté.
      if (request.type === 'alert') resolve(window.alert(request.message));
      else if (request.type === 'confirm') resolve(window.confirm(request.message));
      else resolve(window.prompt(request.message, request.defaultValue ?? ''));
    }
  });
}

/** @returns {Promise<void>} */
export const appAlert = (message, opts = {}) => ask({ type: 'alert', message, ...opts });

/** @returns {Promise<boolean>} */
export const appConfirm = (message, opts = {}) => ask({ type: 'confirm', message, ...opts });

/** @returns {Promise<string|null>} */
export const appPrompt = (message, defaultValue = '', opts = {}) =>
  ask({ type: 'prompt', message, defaultValue, ...opts });
